import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

// Create axios instance with default config
const taskAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/tasks`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
taskAPI.interceptors.request.use((config) => {
  const token = Cookies.get('sankalvax_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
taskAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired, redirect to login
      Cookies.remove('sankalvax_token');
      Cookies.remove('sankalvax_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const taskService = {
  // Task CRUD operations
  async getTasks(filters = {}) {
    const params = new URLSearchParams();

    // Add filters to params
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });

    const queryString = params.toString();
    const response = await taskAPI.get(queryString ? `?${queryString}` : '');
    return response.data;
  },

  async createTask(taskData) {
    const response = await taskAPI.post('', taskData);
    return response.data;
  },

  async getTask(taskId) {
    const response = await taskAPI.get(`/${taskId}`);
    return response.data;
  },

  async updateTask(taskId, taskData) {
    const response = await taskAPI.put(`/${taskId}`, taskData);
    return response.data;
  },

  async deleteTask(taskId) {
    const response = await taskAPI.delete(`/${taskId}`);
    return response.data;
  },

  // Task status and assignment
  async updateTaskStatus(taskId, status) {
    const response = await taskAPI.put(`/${taskId}/status`, { status });
    return response.data;
  },

  async assignTask(taskId, assignedTo) {
    const response = await taskAPI.post(`/${taskId}/assign`, { assigned_to: assignedTo });
    return response.data;
  },

  // My tasks
  async getMyTasks(status = null) {
    const params = status ? `?status=${status}` : '';
    const response = await taskAPI.get(`/my/tasks${params}`);
    return response.data;
  },

  // Notifications
  async getNotifications(unreadOnly = false) {
    const params = new URLSearchParams();
    if (unreadOnly !== null && unreadOnly !== undefined) {
      params.append('unread_only', unreadOnly.toString());
    }
    const response = await taskAPI.get(`/notifications?${params.toString()}`);
    return response.data;
  },

  async markNotificationRead(notificationId) {
    const response = await taskAPI.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Comments
  async getTaskComments(taskId) {
    const response = await taskAPI.get(`/${taskId}/comments`);
    return response.data;
  },

  async addTaskComment(taskId, comment) {
    const response = await taskAPI.post(`/${taskId}/comments`, { comment });
    return response.data;
  },

  // Users for assignment
  async getUsersForAssignment() {
    const response = await taskAPI.get('/users');
    return response.data;
  },
};

// Export individual functions for convenience
export const {
  getTasks,
  createTask,
  getTask,
  updateTask,
  deleteTask,
  updateTaskStatus,
  assignTask,
  getMyTasks,
  getNotifications,
  markNotificationRead,
  getTaskComments,
  addTaskComment,
  getUsersForAssignment,
} = taskService;